import { useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { CreditCard, ChevronRight, Plus } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { PageHeader } from '@/components/ui/page-header'
import { EmptyState } from '@/components/ui/empty-state'
import { TableSkeleton } from '@/components/ui/skeleton'
import { SortHead, useTableSort, cmp } from '@/components/ui/sortable-head'
import { useAccounts, useCardMonthFatura, useCardTotalDebt } from '@/hooks/useAccounts'
import { useCurrentRates, convertWithRates } from '@/hooks/useCurrentRates'
import { useHousehold } from '@/context/HouseholdContext'
import { useT } from '@/i18n/I18nProvider'
import { formatCurrency } from '@/lib/currency'
import { dueDateForMonth, dueISOForMonth } from '@/lib/cards'
import { accountIcon } from '@/lib/accountIcon'
import { AccountFormDialog } from '@/components/accounts/AccountFormDialog'
import type { Account } from '@/types/db'

// Fatura do MÊS ATUAL por cartão (compras − estornos), na moeda do cartão e na base.
function currentMonthBounds() {
  const now = new Date()
  const first = new Date(now.getFullYear(), now.getMonth(), 1)
  const last = new Date(now.getFullYear(), now.getMonth() + 1, 0)
  const iso = (x: Date) => x.toISOString().slice(0, 10)
  return { from: iso(first), to: iso(last), monthKey: iso(first).slice(0, 7), year: now.getFullYear(), month0: now.getMonth() }
}

export function CardsPage() {
  const { t } = useT()
  const { baseCurrency } = useHousehold()
  const navigate = useNavigate()
  const [dialogOpen, setDialogOpen] = useState(false)
  const { data: accounts, isLoading } = useAccounts()
  // Coluna "fatura" = o que é COBRADO no mês (régua byStatement), igual à tela do
  // cartão. A coluna "total" ao lado continua sendo a dívida real (todas as datas).
  const { data: cardFatura } = useCardMonthFatura(undefined, undefined, true)
  const { data: cardTotal } = useCardTotalDebt()
  const { data: rates } = useCurrentRates()
  const { monthKey, year, month0 } = useMemo(currentMonthBounds, [])

  const cards = useMemo(() => (accounts ?? []).filter((a) => a.type === 'cartao'), [accounts])

  // Fatura EM ABERTO do mês (dívida no fim do mês, líquida de pagamentos).
  const faturaNative = (c: Account) => cardFatura?.get(c.id) ?? 0
  // Dívida TOTAL do cartão (todos os meses/status não pagos) — saldo all-status (neg=dívida).
  const totalNative = (c: Account) => Math.max(-(cardTotal?.get(c.id) ?? 0), 0)
  const totalBase = (c: Account) => convertWithRates(totalNative(c), c.currency, rates) ?? 0

  type CKey = 'card' | 'due' | 'invoice' | 'total' | 'base'
  const { sort, toggle } = useTableSort<CKey>('total', 'desc', ['invoice', 'total', 'base'])
  const sortedCards = useMemo(() => {
    const val = (c: Account): string | number => {
      switch (sort.key) {
        case 'card':
          return c.name
        case 'due':
          return dueISOForMonth(c.due_day, year, month0) ?? ''
        case 'invoice':
          return faturaNative(c)
        case 'total':
          return totalNative(c)
        case 'base':
          return totalBase(c)
      }
    }
    return cards.slice().sort((a, b) => cmp(val(a), val(b), sort.dir))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cards, cardFatura, cardTotal, rates, sort, year, month0])

  return (
    <div className="mx-auto max-w-5xl space-y-6">
      <PageHeader
        title={t('nav.cards')}
        subtitle={t('acct.cardsSubtitle')}
        actions={
          <Button onClick={() => setDialogOpen(true)}>
            <Plus className="size-4" /> {t('acct.newCard')}
          </Button>
        }
      />

      <Card>
        <CardContent className="p-0">
          {isLoading || accounts === undefined ? (
            <TableSkeleton rows={6} cols={4} />
          ) : cards.length === 0 ? (
            <EmptyState
              icon={CreditCard}
              title={t('acct.cardsEmptyTitle')}
              description={t('acct.cardsEmptyDesc')}
              action={
                <Button onClick={() => setDialogOpen(true)}>
                  <Plus className="size-4" /> {t('acct.newCard')}
                </Button>
              }
            />
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <SortHead label={t('acct.colCard')} k="card" sort={sort} onToggle={toggle} />
                    <SortHead label={t('acct.colDueDate')} k="due" sort={sort} onToggle={toggle} />
                    <SortHead label={t('acct.colMonthInvoice')} k="invoice" sort={sort} onToggle={toggle} className="text-right" />
                    <SortHead label={t('acct.colTotalDebt')} k="total" sort={sort} onToggle={toggle} className="text-right" />
                    <SortHead label={t('acct.colTotalIn', { currency: baseCurrency })} k="base" sort={sort} onToggle={toggle} className="text-right" />
                    <TableHead className="w-10" />
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {sortedCards.map((c) => {
                    const nat = faturaNative(c)
                    const tot = totalNative(c)
                    const totBas = totalBase(c)
                    // a fatura é nomeada pelo mês em que vence ⇒ due_day deste mês
                    const due = dueDateForMonth(c.due_day, year, month0)
                    return (
                      <TableRow
                        key={c.id}
                        className="hover:bg-accent/40 cursor-pointer"
                        onClick={() => navigate(`/cartoes/${c.id}?m=${monthKey}`)}
                      >
                        <TableCell className="font-medium">
                          <span className="inline-flex items-center gap-1.5">
                            <span className="text-base leading-none">{accountIcon(c)}</span> {c.name}
                          </span>
                        </TableCell>
                        <TableCell className="whitespace-nowrap tabular-nums">
                          {due ?? <span className="text-muted-foreground">—</span>}
                        </TableCell>
                        <TableCell className="text-right tabular-nums whitespace-nowrap">
                          {nat > 0.005 ? (
                            <span className="text-destructive">{formatCurrency(nat, c.currency)}</span>
                          ) : (
                            <span className="text-muted-foreground">—</span>
                          )}
                        </TableCell>
                        <TableCell className="text-right tabular-nums whitespace-nowrap">
                          {tot > 0.005 ? (
                            <span className="text-destructive font-medium">{formatCurrency(tot, c.currency)}</span>
                          ) : (
                            <span className="text-muted-foreground">—</span>
                          )}
                        </TableCell>
                        <TableCell className="text-right tabular-nums whitespace-nowrap">
                          {c.currency !== baseCurrency && tot > 0.005 ? (
                            formatCurrency(totBas, baseCurrency)
                          ) : (
                            <span className="text-muted-foreground">—</span>
                          )}
                        </TableCell>
                        <TableCell className="text-right">
                          <ChevronRight className="text-muted-foreground ml-auto size-4" />
                        </TableCell>
                      </TableRow>
                    )
                  })}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      <AccountFormDialog open={dialogOpen} onOpenChange={setDialogOpen} defaultType="cartao" />
    </div>
  )
}
